import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone as _lr_Phone, PhoneOff as _lr_PhoneOff, Mic as _lr_Mic, MicOff as _lr_MicOff, Volume2 as _lr_Volume2, Loader2 as _lr_Loader2 } from 'lucide-react';
import Button from './ui/Button';
import Avatar from './ui/Avatar';

const Phone = _lr_Phone || (() => null);
const PhoneOff = _lr_PhoneOff || (() => null);
const Mic = _lr_Mic || (() => null);
const MicOff = _lr_MicOff || (() => null);
const Volume2 = _lr_Volume2 || (() => null);
const Loader2 = _lr_Loader2 || (() => null);
const SCRIPT = [
  { speaker: 'bot', text: "Hi {name}! I'm HireBot, an AI recruiter for the {role} position. Is now a good time to chat for about 10 minutes?" },
  { speaker: 'candidate', text: 'Sure, I have some time now.' },
  { speaker: 'bot', text: 'Great! Can you walk me through your most recent project and the technologies you used?' },
  { speaker: 'candidate', text: 'I led a React + TypeScript migration at my last company, improving performance by 40%.' },
  { speaker: 'bot', text: 'Impressive! How do you handle state management in large-scale applications?' },
  { speaker: 'candidate', text: 'I prefer Zustand for simplicity, but I have deep experience with Redux Toolkit as well.' },
  { speaker: 'bot', text: 'Perfect. One last question — what\'s your expected salary range for this role?' },
  { speaker: 'candidate', text: 'I\'m looking at $150K–$170K depending on the full package.' },
  { speaker: 'bot', text: 'Thank you so much! Your responses have been recorded. Our team will follow up within 2 business days. Have a great day!' },
];

export function CallModal({ candidate = {}, onClose }) {
  const [phase, setPhase] = useState('ringing'); // ringing | active | ended
  const [msgIdx, setMsgIdx] = useState(0);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState([]);

  useEffect(() => {
    if (phase === 'ringing') {
      const t = setTimeout(() => setPhase('active'), 2500);
      return () => clearTimeout(t);
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== 'active') return;
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'active') return;
    if (msgIdx >= SCRIPT.length) {
      const t = setTimeout(() => setPhase('ended'), 1500);
      return () => clearTimeout(t);
    }
    
    const msg = SCRIPT[msgIdx];
    const text = msg.text.replace('{name}', candidate.name || 'there').replace('{role}', candidate.role || 'this role');
    const delay = msgIdx === 0 ? 1000 : 2500;
    
    const t = setTimeout(() => {
      setTranscript(prev => [...prev, { ...msg, text }]);
      setMsgIdx(i => i + 1);
    }, delay);
    
    return () => clearTimeout(t);
  }, [phase, msgIdx, candidate.name, candidate.role]);

  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <motion.div 
        initial={{ scale: 0.9, opacity: 1, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-white/20"
      >
        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-800 p-8 text-white text-center relative">
          <div className="absolute top-4 right-4">
             <div className="flex items-center gap-1.5 bg-white/10 px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                <div className={`w-1.5 h-1.5 rounded-full ${phase === 'active' ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`} />
                {phase}
             </div>
          </div>
          <div className="relative inline-block">
            <Avatar src={candidate.avatar} name={candidate.name || ''} size="lg" className="mx-auto mb-4 ring-4 ring-white/20" />
            {phase === 'active' && (
               <motion.div 
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
                className="absolute inset-0 rounded-full border-2 border-white/30"
               />
            )}
          </div>
          <h3 className="font-bold text-xl">{candidate.name || 'Candidate'}</h3>
          <p className="text-purple-100 text-sm opacity-80">{candidate.role || 'Recruitment Call'}</p>
          <div className="mt-4 text-2xl font-mono font-light tracking-widest">
            {phase === 'ringing' && <span className="flex items-center justify-center gap-2"><Loader2 className="animate-spin" size={20} /> Connecting...</span>}
            {phase === 'active' && <span>{fmt(elapsed)}</span>}
            {phase === 'ended' && <span className="text-emerald-300">Call Ended</span>}
          </div>
        </div>

        <div className="p-6 h-64 overflow-y-auto space-y-4 bg-gray-50/50">
          <AnimatePresence initial={false}>
            {transcript.map((msg, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 1, x: msg.speaker === 'bot' ? -10 : 10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`flex ${msg.speaker === 'bot' ? 'justify-start' : 'justify-end'}`}
              >
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${ 
                  msg.speaker === 'bot' 
                    ? 'bg-white text-gray-800 rounded-tl-none border border-gray-100' 
                    : 'bg-purple-600 text-white rounded-tr-none'
                }`}>
                  <span className={`text-xs font-bold block mb-1 uppercase tracking-tighter ${msg.speaker === 'bot' ? 'text-purple-600' : 'text-purple-100'}`}>
                    {msg.speaker === 'bot' ? 'AI Assistant' : 'Candidate'}
                  </span>
                  {msg.text}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {phase === 'ringing' && (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 space-y-2">
              <Phone className="animate-bounce" />
              <p className="text-sm">Initiating Vapi.ai voice stream...</p>
            </div>
          )}
        </div>

        <div className="p-6 flex items-center justify-center gap-6 bg-white border-t border-gray-100">
          {phase === 'active' && (
            <motion.button 
              whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              onClick={() => setMuted(m => !m)} 
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${ 
                muted ? 'bg-red-100 text-red-600 shadow-inner' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {muted ? <MicOff size={20} /> : <Mic size={20} />}
            </motion.button>
          )}
          
          <motion.button 
            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
            onClick={onClose} 
            className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-200 transition-transform"
          >
            <PhoneOff size={28} />
          </motion.button>

          {phase === 'active' && (
            <motion.button 
              whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center transition-all"
            >
              <Volume2 size={20} />
            </motion.button>
          )}
          
          {phase === 'ended' && (
            <Button className="absolute bottom-8 right-8 shadow-xl" onClick={onClose}>
              Save Transcript
            </Button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default CallModal;